import React, { Component } from 'react'
import { throttle, isEqual } from 'lodash'
import PropTypes from 'prop-types'

// Gets the position of the element in reference to the DOM
const getTopPosition = elem =>
  elem.getBoundingClientRect().top + window.pageYOffset

// Context contains information about all of it's immediate children ScrollSpy components.
// We need this state to keep track of the offsetTops for *all* elements to
// determine which one is the bottom-most element (ie which one is "active")
class ScrollSpyContext extends Component {
  static propTypes = {
    throttleRate: PropTypes.number,
    overwriteHash: PropTypes.bool,
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node),
    ]),
  }

  static defaultProps = {
    throttleRate: 16, // Runs a max of 60FPS by default
  }

  state = {
    items: [],
    selectedItems: [],
  }

  scrollEvent = null

  componentDidMount() {
    const { throttleRate } = this.props
    this.onScroll() // Get the initial event
    this.scrollEvent = throttle(this.onScroll, 1000 / throttleRate, {
      trailing: true,
    })
    window.addEventListener('scroll', this.scrollEvent)
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.scrollEvent)
  }

  onScroll = () => {
    const { overwriteHash } = this.props
    const largestOffset = this.state.items.reduce((largestOffset, item) => {
      // We need to query these each time because we don't know if they've been removed since the last time
      // onScroll was called
      const elem = document.querySelector(item.selector)
      if (elem) {
        item.offsetTop = getTopPosition(elem) - item.offset - 1 // To make sure we are always INSIDE the desired range
        return item.offsetTop > largestOffset &&
          window.pageYOffset >= item.offsetTop
          ? item.offsetTop
          : largestOffset
      } else {
        return largestOffset
      }
    }, -Infinity)
    const selectedItems = this.state.items.filter(
      item => item.offsetTop === largestOffset
    )

    // Update hash in URL
    const selectedHref =
      selectedItems.length > 0 ? selectedItems[0].selector : ''
    if (
      !!overwriteHash &&
      !!selectedHref &&
      selectedHref !== window.location.hash
    ) {
      history.pushState(null, null, selectedHref)
    }

    // Update selectedItems, causing a rerender if shouldComponentUpdate catches a change
    this.setState({
      selectedItems,
    })
  }

  // Only re-render when selected values have changed
  shouldComponentUpdate(_nextProps, nextState) {
    const { selectedItems: nextSelectedItems } = nextState
    const { selectedItems } = this.state
    return !isEqual(selectedItems, nextSelectedItems)
  }

  // Keeps items updated based on children
  static getDerivedStateFromProps(nextProps, prevState) {
    const children = nextProps.children
    const items = React.Children.map(children, child => {
      if (child.type === ScrollSpy) {
        // Default applied here (not via defaultProps) because ScrollSpy's own
        // param defaults don't surface on child.props read by this parent.
        const { selector, offset = 70 } = child.props
        return { selector, offset }
      }
    })
    return {
      ...prevState,
      items,
    }
  }

  render() {
    const children = this.props.children
    const { selectedItems } = this.state
    return React.Children.map(children, child => {
      if (child.type !== ScrollSpy) {
        return child
      }

      // Update the "active" prop of scrollspy components
      const foundSelected = selectedItems.find(
        ({ selector, offset }) =>
          selector === child.props.selector &&
          offset === (child.props.offset ?? 70)
      )
      return React.cloneElement(child, {
        active: !!foundSelected,
      })
    })
  }
}

/**
 * @typedef {object} ScrollSpyProps
 * @property {number} [offset] - px from top to trigger active state (default: 70)
 * @property {string} selector - CSS selector for the target element
 * @property {(props: {active: boolean, scrollTo: () => void}) => React.ReactElement | null} children - render prop
 * @property {boolean} [active]
 */
/** @type {React.NamedExoticComponent<ScrollSpyProps> & {Context: typeof ScrollSpyContext}} */
const ScrollSpy = React.memo(
  (
    /** @type {ScrollSpyProps} */ {
      selector,
      offset = 70,
      children,
      active = false,
      ...rest
    }
  ) => {
    const scrollToElement = () => {
      const elem = window.document.querySelector(selector)
      if (elem) {
        window.scrollTo({
          top: getTopPosition(elem) - offset,
          behavior: 'smooth',
        })
      }
    }

    return children({
      ...rest,
      active,
      scrollTo: scrollToElement,
    })
  }
)

ScrollSpy.Context = ScrollSpyContext

export default ScrollSpy
